import React from "react";
import { connect } from "react-redux";
import moment from "moment";

import { Input } from "../../Input";
import { Button } from "../../Button";
import language from "../../../language";
import { getStorage } from "../../../utils/storage";
import { rescheduleBooking } from "../../../utils/queries/booking-prompt";
import toastMessage from "../../../utils/toastMessage";

// datetime-local will not accept an ISO string with a zone on it.
const toInputValue = (value) =>
  value ? moment(value).format("YYYY-MM-DDTHH:mm") : "";

/**
 * The booked time of a request, with an edit control for the doctor who raised
 * it. Read-only for everyone else, which is how it behaved before.
 *
 * Editable at any stage, as a booking that has already reached the laboratory
 * is exactly the one most likely to need moving. The server re-checks who is
 * asking and tells the laboratory about the new time, so this is only the way
 * in, never the authority.
 */
class BookingTimeEditor extends React.Component {
  state = {
    user: {},
    isEditing: false,
    value: "",
    isSubmitting: false,
  };

  componentDidMount = async () => {
    const user = await getStorage();

    this.setState({ user, value: toInputValue(this.props.request?.bookingDate) });
  };

  componentDidUpdate(prevProps) {
    if (prevProps.request?.bookingDate !== this.props.request?.bookingDate) {
      this.setState({ value: toInputValue(this.props.request?.bookingDate) });
    }
  }

  // Bound to the account that created the request rather than the typed-in
  // doctor name, which is free text and means nothing for authorisation.
  canEdit = () => {
    const { request } = this.props;
    const { user } = this.state;

    if (!request?._id || !user?.id) return false;

    const status = String(request.status || "").toLowerCase();

    if (status === "cancelled" || status === "completed") return false;

    const owner = request.account?._id || request.account;

    return String(owner || "") === String(user.id);
  };

  handleSave = async () => {
    const { value } = this.state;

    if (!value) {
      toastMessage("error", "Please choose a new scheduled time");
      return;
    }

    this.setState({ isSubmitting: true });

    try {
      const result = await rescheduleBooking({
        id: this.props.request._id,
        bookingDate: moment(value).toISOString(),
      });

      toastMessage(
        "success",
        result?.message || "Scheduled time updated"
      );

      this.setState({ isEditing: false, isSubmitting: false });

      if (this.props.onUpdated) this.props.onUpdated(value);
    } catch (error) {
      this.setState({ isSubmitting: false });
      toastMessage(
        "error",
        error?.message || "Could not update the scheduled time"
      );
    }
  };

  render() {
    const { request, defaultLanguage } = this.props;
    const { isEditing, value, isSubmitting } = this.state;

    if (!request) return null;

    const label = language[defaultLanguage]?.booking || "Booking";

    if (!isEditing) {
      return (
        <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
          <div style={{ flex: 1 }}>
            <Input
              label={label}
              value={
                request.bookingDate
                  ? moment(request.bookingDate).format("lll")
                  : ""
              }
              disabled
            />
          </div>
          {this.canEdit() && (
            <div style={{ paddingBottom: 12 }}>
              <Button
                text={language[defaultLanguage]?.edit || "Edit"}
                onPress={() => this.setState({ isEditing: true })}
              />
            </div>
          )}
        </div>
      );
    }

    return (
      <div>
        <Input
          label={label}
          type="datetime-local"
          value={value}
          min={moment().format("YYYY-MM-DDTHH:mm")}
          onChange={(e) => this.setState({ value: e?.target?.value })}
        />
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            text={language[defaultLanguage]?.save || "Save"}
            isSubmitting={isSubmitting}
            onPress={this.handleSave}
          />
          <Button
            text={language[defaultLanguage]?.cancel || "Cancel"}
            className="btn-default"
            onPress={() =>
              this.setState({
                isEditing: false,
                value: toInputValue(request.bookingDate),
              })
            }
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;

  return { defaultLanguage };
};

export default connect(mapStateToProps)(BookingTimeEditor);
